/**
 * auth layout 右欄要顯示什麼
 *
 * 登入／註冊／忘記密碼放 lottie 動畫，組織設定與處理中放引導圖。這份內容是「頁面決定的」，
 * 但畫它的地方在 layout —— 頁面在 setup 裡宣告要什麼，layout 讀這裡渲染。
 *
 * 為什麼不用具名 slot：那需要頁面自己掛 <NuxtLayout>（配 layout: false），等於把版面變成
 * 頁面內容的一部分，換頁時版面的存活就跟著 <NuxtPage> 的 Suspense 走，而不是交給 layout
 * 系統管理。改成這裡之後，auth 版面就是一個正常的 Nuxt layout。
 */

export type AuthLayoutMedia = { type: 'animation' } | { type: 'image'; src: string };

/**
 * 組織設定流程的引導圖（assets/images/wizard/login01~04.png）
 *
 * 走 import.meta.glob 讓打包器解析：assets/ 底下的檔案不是 public 資源，執行期用
 * `/assets/...` 這種字串路徑在 production 取不到（打包後是 /_nuxt/ 下的雜湊檔名），
 * 在伺服器端更會被 new URL(..., import.meta.url) 解析成 file:// 而被瀏覽器擋下。
 */
const wizardImages: string[] = Object.entries(
  import.meta.glob<string>('../assets/images/wizard/login*.png', { eager: true, import: 'default' })
)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);

const wizardImageUrl = (index: number): string =>
  wizardImages[Math.min(Math.max(index, 1), wizardImages.length) - 1] ?? '';

export function useAuthLayoutMedia() {
  const media = useState<AuthLayoutMedia>('auth-layout-media', () => ({ type: 'animation' }));

  /** 登入／註冊／忘記密碼：lottie 動畫 */
  const showAnimation = () => {
    media.value = { type: 'animation' };
  };

  /** 組織設定流程：指定一張引導圖（預設第一張，也是圖片載入失敗時的退路） */
  const showWizardImage = (index = 1) => {
    media.value = { type: 'image', src: wizardImageUrl(index) };
  };

  /**
   * 隨機挑一張引導圖
   *
   * 只在 onMounted 之後呼叫 —— 隨機值在伺服器端與瀏覽器端會挑到不同張，
   * 放進 SSR 會造成 hydration mismatch。SSR 那一輪先用 showWizardImage() 給固定的第一張。
   */
  const showRandomWizardImage = () => {
    showWizardImage(Math.floor(Math.random() * wizardImages.length) + 1);
  };

  return { media, showAnimation, showWizardImage, showRandomWizardImage };
}
